import React, { useState, useEffect, useRef } from 'react';
import { Badge, IconButton, Popover, List, ListItem, ListItemText, Typography, Box, Divider, Button, CircularProgress } from '@mui/material';
import { Notifications, NotificationsNone, Circle, CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const NotificationBell = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificaciones, setNotificaciones] = useState([]);
  const [contador, setContador] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  // Cargar contador de notificaciones no leídas
  const cargarContador = async () => {
    try {
      const response = await axios.get('/api/notificaciones/contador-no-leidas');
      setContador(response.data.contador || 0);
    } catch (error) {
      console.error('Error al cargar contador:', error);
    }
  };

  // Cargar notificaciones
  const cargarNotificaciones = async (pageNum = 1, append = false) => {
    if (loading) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`/api/notificaciones/mis-notificaciones?limit=10`);
      const nuevasNotificaciones = response.data.notificaciones || [];
      
      console.log('Notificaciones cargadas:', nuevasNotificaciones.length);
      
      if (append) {
        setNotificaciones(prev => [...prev, ...nuevasNotificaciones]);
      } else {
        setNotificaciones(nuevasNotificaciones);
      }
      
      setHasMore(nuevasNotificaciones.length === 10);
      setPage(pageNum);
    } catch (error) {
      console.error('Error al cargar notificaciones:', error);
    } finally {
      setLoading(false);
    }
  };

  // Marcar notificación como leída y navegar
  const handleNotificacionClick = async (notificacion) => {
    try {
      if (!notificacion.leida) {
        await axios.put(`/api/notificaciones/${notificacion.id}/marcar-leida`);
        setContador(prev => Math.max(0, prev - 1));
        setNotificaciones(prev => 
          prev.map(n => n.id === notificacion.id ? { ...n, leida: true } : n)
        );
      }
      
      if (notificacion.url) {
        navigate(notificacion.url);
      }
      
      handleClose();
    } catch (error) {
      console.error('Error al marcar como leída:', error);
    }
  };

  // Marcar todas como leídas
  const handleMarcarTodasLeidas = async () => {
    try {
      await axios.put('/api/notificaciones/marcar-todas-leidas');
      setContador(0);
      setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })));
    } catch (error) {
      console.error('Error al marcar todas como leídas:', error);
    }
  };

  // Abrir/cerrar popover
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    if (!anchorEl) {
      cargarNotificaciones(1, false);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Cargar más notificaciones (scroll infinito)
  const handleCargarMas = () => {
    if (hasMore && !loading) {
      cargarNotificaciones(page + 1, true);
    }
  };

  // Polling para actualizar contador cada 10 segundos
  useEffect(() => {
    cargarContador();
    
    intervalRef.current = setInterval(() => {
      cargarContador();
    }, 10000); // 10 segundos

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Formatear tiempo relativo
  const formatearTiempo = (fecha) => {
    const ahora = new Date();
    const fechaNotif = new Date(fecha);
    const diff = Math.floor((ahora - fechaNotif) / 1000); // diferencia en segundos

    if (diff < 60) return 'Hace un momento';
    if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
    if (diff < 604800) return `Hace ${Math.floor(diff / 86400)} días`;
    
    return fechaNotif.toLocaleDateString();
  };

  // Obtener color según tipo de notificación
  const getColorTipo = (tipo) => {
    switch (tipo) {
      case 'vacacion_aprobada_referente':
      case 'vacacion_aprobada_rh':
        return '#4caf50';
      case 'vacacion_rechazada_referente':
      case 'vacacion_rechazada_rh':
        return '#f44336';
      case 'recibo_subido':
        return '#2196f3';
      case 'vacacion_creada':
        return '#ff9800';
      default:
        return '#9e9e9e';
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{ 
          mx: 1,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <Badge badgeContent={contador} color="error" max={99}>
          {contador > 0 ? <Notifications /> : <NotificationsNone />}
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            mt: 1
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Notificaciones</Typography>
          {contador > 0 && (
            <Button 
              size="small" 
              onClick={handleMarcarTodasLeidas}
              startIcon={<CheckCircle />}
            >
              Marcar todas leídas
            </Button>
          )}
        </Box>
        
        <Divider />

        {notificaciones.length === 0 && !loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <NotificationsNone sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No tenés notificaciones
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 450, overflow: 'auto', p: 0 }}>
            {notificaciones.map((notif, index) => (
              <React.Fragment key={notif.id}>
                <ListItem
                  button
                  onClick={() => handleNotificacionClick(notif)}
                  sx={{
                    backgroundColor: notif.leida ? 'transparent' : 'action.hover',
                    '&:hover': {
                      backgroundColor: notif.leida ? 'action.hover' : 'action.selected',
                    },
                    cursor: 'pointer'
                  }}
                >
                  <Circle 
                    sx={{ 
                      fontSize: 10, 
                      mr: 2, 
                      color: getColorTipo(notif.tipo),
                      opacity: notif.leida ? 0.3 : 1
                    }} 
                  />
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" sx={{ fontWeight: notif.leida ? 400 : 600 }}>
                        {notif.titulo}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {notif.mensaje}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {formatearTiempo(notif.fecha_creacion)}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                {index < notificaciones.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
            
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            
            {hasMore && !loading && notificaciones.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <Button size="small" onClick={handleCargarMas}>
                  Cargar más
                </Button>
              </Box>
            )}
          </List>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
